import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import {
  ONE_HOUR_MS,
  STALE_TIME,
} from '@/shared/constants/app/time.constant.ts';

import { pumsemManageApi } from './pumsem-manage.api.ts';
import { pumsemApi } from './pumsem.api.ts';
import { pumsemKeys } from './pumsem.keys.ts';
import type { PumsemSearchParams } from '../model/pumsem.type';

/** 개정판 목록. 반기마다 한 번 늘어나는 데이터라 오래 붙잡아 둔다. */
export function usePumsemEditions() {
  return useQuery({
    queryKey: pumsemKeys.editions(),
    queryFn: () => pumsemApi.getEditions(),
    staleTime: STALE_TIME.LONG,
    gcTime: ONE_HOUR_MS,
  });
}

/**
 * 부문 → 장 → 절 → 항 트리. 검색 전 첫 화면이 이걸로 훑기를 제공한다.
 *
 * 숨긴 판을 보고 있으면 관리 경로로 부른다 — 공개 경로는 그 판의 존재 자체를
 * 모른다.
 */
export function usePumsemTree(edition: string | null, asAdmin = false) {
  return useQuery({
    queryKey: [...pumsemKeys.all, 'tree', edition, asAdmin] as const,
    queryFn: () =>
      asAdmin
        ? pumsemManageApi.getTree(edition ?? undefined)
        : pumsemApi.getTree(edition ?? undefined),
    staleTime: STALE_TIME.LONG,
    gcTime: ONE_HOUR_MS,
    retry: false,
  });
}

/** 한 번에 받아오는 결과 수. 서버 `SEARCH_PAGE_SIZE` 와 같아야 한다. */
export const PUMSEM_SEARCH_PAGE = 30;

/**
 * 검색.
 *
 * `keepPreviousData` 를 쓴다 — 글자를 한 자 칠 때마다 목록이 스켈레톤으로
 * 비워지면 방금 본 결과가 사라져 읽던 자리를 잃는다. 이전 결과를 남기고
 * 위에 로딩만 표시하는 쪽이 타이핑 검색에서 훨씬 낫다.
 *
 * `useInfiniteQuery` 인 이유: 예전엔 첫 페이지만 받고 그 뒤로 갈 길이 없었다.
 * `1,284건` 이라고 써 놓고 30장만 그리면 실무자는 그 30건이 전부라고 읽거나
 * 없는 항목을 찾으려고 검색어만 계속 바꾼다. 오프셋 state 를 손으로 들고
 * 이어붙이면 검색어가 바뀔 때 초기화를 놓치기 쉬워서 훅에 맡긴다.
 */
export function usePumsemSearch(
  params: PumsemSearchParams,
  enabled = true,
  /**
   * 숨긴 판을 보고 있으면 관리 경로로 부른다. 공개 경로는 그 판의 존재 자체를
   * 모르므로 404 가 난다 — 키에도 넣어서 두 출처의 결과가 섞이지 않게 한다.
   */
  asAdmin = false,
) {
  return useInfiniteQuery({
    queryKey: [...pumsemKeys.search(params), asAdmin] as const,
    queryFn: ({ pageParam }) => {
      const page = { ...params, offset: pageParam, limit: PUMSEM_SEARCH_PAGE };
      return asAdmin ? pumsemManageApi.search(page) : pumsemApi.search(page);
    },
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((count, page) => count + page.items.length, 0);
      // 서버가 준 총계보다 적게 받았을 때만 다음 장이 있다. 빈 페이지가 오면
      // (총계와 실제가 어긋나는 경우) 여기서 멈춘다 — 아니면 무한히 부른다.
      return loaded < last.total && last.items.length > 0 ? loaded : undefined;
    },
    enabled: enabled && params.q.trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME.SHORT,
  });
}

/** 항목 상세. `edition` 이 없으면 서버가 최신 발행판으로 답한다. */
export function usePumsemItem(
  division: string | undefined,
  code: string | undefined,
  edition: string | null,
  /** 숨긴 판을 보고 있으면 관리 경로로 부른다(`usePumsemAdminOverlay`). */
  asAdmin = false,
  /**
   * 코드가 **장·절**이면 끈다. 상세 API 에는 없는 코드라 확정 404 인데,
   * 트리에 그 마디가 있으면 화면은 목차를 그린다(`PumsemGroupView`).
   */
  enabled = true,
) {
  return useQuery({
    queryKey: [...pumsemKeys.item(division ?? '', code ?? '', edition), asAdmin] as const,
    queryFn: () =>
      asAdmin
        ? pumsemManageApi.getItem(division!, code!, edition ?? undefined)
        : pumsemApi.getItem(division!, code!, edition ?? undefined),
    enabled: enabled && !!division && !!code,
    staleTime: STALE_TIME.LONG,
    gcTime: ONE_HOUR_MS,
    // 없는 코드는 재시도할 이유가 없다. 404 판정이 그만큼 늦어질 뿐이다.
    retry: false,
  });
}

/** 직전 판 대비 비교. 임의 두 판 비교는 서버가 제공하지 않는다. */
export function usePumsemDiff(
  division: string | undefined,
  code: string | undefined,
  edition: string | null,
) {
  return useQuery({
    queryKey: pumsemKeys.diff(division ?? '', code ?? '', edition),
    queryFn: () => pumsemApi.getDiff(division!, code!, edition ?? undefined),
    enabled: !!division && !!code,
    staleTime: STALE_TIME.LONG,
    retry: false,
  });
}
